import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, interval, Observable, of, shareReplay, startWith, timer } from 'rxjs';
import { WatchdogService } from './watchdog.service';
import { Intercom } from './intercom.service';
import { delayWhen, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsocketService } from './websocket.service';
import { RestaurantTableService } from './restaurant-table.service';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { RGBA } from 'color-blend/dist/types';
import { IFeature, IRestaurantTable } from '../models';
import { ClusterService } from './cluster.service';
import { NetworkService } from './network.service';
import { CORE_FEATURE_TOGGLE } from '../../core.tokens';
import { isObject, isTruthy, observeOnAnimationFrame } from '../utils';

export type CallWaiterType = 'main' | 'secondary' | 'tertiary';

type CallWaiterFeatureKey = 'withCallWaiter' | 'withCallWaiterToPay' | 'withCallWaiterToRepeat';

interface CallWaiterDefinition {
  readonly type: CallWaiterType;
  readonly featureKey: CallWaiterFeatureKey;
}

const CALL_WAITER_DEFINITIONS = [
  {
    type: 'main',
    featureKey: 'withCallWaiter',
  },
  {
    type: 'secondary',
    featureKey: 'withCallWaiterToPay',
  },
  {
    type: 'tertiary',
    featureKey: 'withCallWaiterToRepeat',
  },
] as const satisfies readonly CallWaiterDefinition[];

const CALL_WAITER_DEFINITION_BY_TYPE = CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
  acc[definition.type] = definition;
  return acc;
}, {} as Record<CallWaiterType, CallWaiterDefinition>);

export type CallWaiterStatus = Record<CallWaiterType, boolean>;

const DEFAULT_CALL_WAITER_ACTION_NAMES: Record<CallWaiterType, string> = {
  main: 'CallWaiter',
  secondary: 'CallWaiterBillPlease',
  tertiary: 'CallWaiterToRepeat',
};

const DEFAULT_PUSH_NOTIFICATION_TEXT: Record<CallWaiterType, string> = {
  main: 'Call the waiter',
  secondary: 'Bill, please',
  tertiary: 'One more round',
};

@Injectable()
export class CallWaiterService {

  private readonly featureToggle = inject(CORE_FEATURE_TOGGLE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly translateLoader = inject(TranslateLoader);
  private readonly cluster = inject(ClusterService);
  private readonly watchdog = inject(WatchdogService);
  private readonly network = inject(NetworkService);
  private readonly intercom = inject(Intercom);
  private readonly webSocket = inject(WebsocketService);
  private readonly restaurantTable = inject(RestaurantTableService);

  private readonly logger = this.watchdog.tag('Call Waiter', 'cyan');

  private readonly statusSubjects = this.createSubjects(false);
  private readonly countSubjects = this.createSubjects(0);
  private readonly lastCallAtSubjects = this.createSubjects<Date | null>(null);
  private readonly actionNamesSubject = new BehaviorSubject<Record<CallWaiterType, string>>(
    DEFAULT_CALL_WAITER_ACTION_NAMES,
  );
  private readonly ambientLightColorSubjects = CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
    acc[definition.type] = new BehaviorSubject<RGBA | null>(
      this.featureToggle.ambientLight.callWaiter[definition.type],
    );
    return acc;
  }, {} as Record<CallWaiterType, BehaviorSubject<RGBA | null>>);

  public readonly statusByType$ = this.createStatusByTypeStreams();

  public readonly mainStatus$ = this.statusByType$.main;
  public readonly secondaryStatus$ = this.statusByType$.secondary;
  public readonly tertiaryStatus$ = this.statusByType$.tertiary;

  public readonly status$ = combineLatest([
    this.mainStatus$,
    this.secondaryStatus$,
    this.tertiaryStatus$,
  ]).pipe(
    map(([main, secondary, tertiary]): CallWaiterStatus => {
      return {
        main,
        secondary,
        tertiary,
      };
    }),
    shareReplay(1),
  );

  public readonly countByType$ = this.createCountByTypeStreams();

  public readonly mainCount$ = this.countByType$.main;
  public readonly secondaryCount$ = this.countByType$.secondary;
  public readonly tertiaryCount$ = this.countByType$.tertiary;

  public readonly count$ = combineLatest([
    this.mainCount$,
    this.secondaryCount$,
    this.tertiaryCount$,
  ]).pipe(
    map(([main, secondary, tertiary]) => {
      return main + secondary + tertiary;
    }),
    shareReplay(1),
  );

  public readonly lastCallAtByType$ = this.createLastCallAtByTypeStreams();

  public readonly mainLastCallAt$ = this.lastCallAtByType$.main;
  public readonly secondaryLastCallAt$ = this.lastCallAtByType$.secondary;
  public readonly tertiaryLastCallAt$ = this.lastCallAtByType$.tertiary;

  public readonly lastCallAt$ = combineLatest([
    this.mainLastCallAt$,
    this.secondaryLastCallAt$,
    this.tertiaryLastCallAt$,
  ]).pipe(
    map(([main, secondary, tertiary]) => {
      return [main, secondary, tertiary].filter((date) => date !== null);
    }),
    map((dates) => {
      if (dates.length === 0) {
        return null;
      }

      return new Date(Math.max(...dates.map((date) => date!.getTime())));
    }),
    shareReplay(1),
  );

  public readonly withMain$ = this.withCallWaiterByType('main');
  public readonly withSecondary$ = this.withCallWaiterByType('secondary');
  public readonly withTertiary$ = this.withCallWaiterByType('tertiary');

  public readonly with$ = combineLatest([
    this.withMain$,
    this.withSecondary$,
    this.withTertiary$,
  ]).pipe(
    map(([main, secondary, tertiary]) => {
      return {
        main,
        secondary,
        tertiary,
      };
    }),
    shareReplay(1),
  );

  public readonly mainAmbientLightColor$ = this.ambientLightColorSubjects.main.asObservable();
  public readonly secondaryAmbientLightColor$ = this.ambientLightColorSubjects.secondary.asObservable();
  public readonly tertiaryAmbientLightColor$ = this.ambientLightColorSubjects.tertiary.asObservable();

  public set mainAmbientLightColor(color: RGBA | null | 'default') {
    if (color === 'default') {
      color = this.featureToggle.ambientLight.callWaiter.main;
    }

    this.ambientLightColorSubjects.main.next(color);
  }

  public get mainAmbientLightColor(): RGBA | null {
    return this.ambientLightColorSubjects.main.value;
  }

  public set secondaryAmbientLightColor(color: RGBA | null | 'default') {
    if (color === 'default') {
      color = this.featureToggle.ambientLight.callWaiter.secondary;
    }

    this.ambientLightColorSubjects.secondary.next(color);
  }

  public get secondaryAmbientLightColor(): RGBA | null {
    return this.ambientLightColorSubjects.secondary.value;
  }

  public set tertiaryAmbientLightColor(color: RGBA | null | 'default') {
    if (color === 'default') {
      color = this.featureToggle.ambientLight.callWaiter.tertiary;
    }

    this.ambientLightColorSubjects.tertiary.next(color);
  }

  public get tertiaryAmbientLightColor(): RGBA | null {
    return this.ambientLightColorSubjects.tertiary.value;
  }

  constructor() {
    this.restaurantTable.table$.pipe(
      map((table) => this.getActionNames(table?.features)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((actionNames) => {
      this.actionNamesSubject.next(actionNames);
    });

    CALL_WAITER_DEFINITIONS.forEach((definition) => {
      this.statusByType$[definition.type].pipe(
        distinctUntilChanged(),
        filter((status) => status),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => {
        const count = this.countSubjects[definition.type];
        count.next(count.value + 1);
        this.lastCallAtSubjects[definition.type].next(new Date());
      });
    });
  }

  public initialize(): void {
    this.logger.info('Initialize');

    this.cluster.leader$.pipe(
      switchMap(() => this.with$),
      distinctUntilChanged(),
      switchMap((enabled) => {
        return this.status$.pipe(
          take(1),
          map((status) => {
            return {
              status,
              enabled,
            };
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ status, enabled }) => {
      if (status.main && !enabled.main) {
        this.setMain(false);
      }

      if (status.secondary && !enabled.secondary) {
        this.setSecondary(false);
      }

      if (status.tertiary && !enabled.tertiary) {
        this.setTertiary(false);
      }
    });

    this.cluster.messages$.pipe(
      filter((message) => message.type === 'waiter.call'),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((message) => {
      if (message.type === 'waiter.call') {
        this.statusSubjects[message.data.type].next(message.data.status);
      }
    });

    // Ambient light
    if (this.featureToggle.ambientLight.glowAnimation === true) {
      combineLatest([
        this.cluster.leader$,
        this.status$,
      ]).pipe(
        map(([leader, status]) => (
          {
            leader,
            color: this.getAmbientLightColor(status),
          }
        )),
        tap(({ color }) => {
          if (color) {
            document.body.style.setProperty(
              '--ambient-light-animation-color',
              `rgba(${ color.r }, ${ color.g }, ${ color.b }, 0.8)`,
            );
            document.body.classList.add('ambient-light-animation');
          }
          else {
            document.body.style.removeProperty('--ambient-light-animation-color');
            document.body.classList.remove('ambient-light-animation');
          }
        }),
        filter(({ leader }) => isTruthy(leader)),
        delayWhen((color) => {
          return timer(color ? 350 : 0);
        }),
        observeOnAnimationFrame(),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(({ color }) => {
        this.sendAmbientLight(color);
      });
    }
    else {
      this.cluster.leader$.pipe(
        filter(isTruthy),
        switchMap(() => this.status$),
        map((status) => this.getAmbientLightColor(status)),
        delayWhen((color) => {
          const delay = color
            ? this.featureToggle.ambientLight.onDelay
            : this.featureToggle.ambientLight.offDelay;

          return timer(delay ?? 0);
        }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe((color) => {
        this.sendAmbientLight(color);
      });
    }

    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.webSocket.messages$),
      filter((message) => message.type === 'echo'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((message) => {
      if (message.data.status === false || message.data.statusMain === false) {
        this.logger.debug('Received echo: Cancel Call Waiter Main.', message.data);
        this.setMain(false);
      }

      if (message.data.pay === false || message.data.statusSecondary === false) {
        this.logger.debug('Received echo: Cancel Call Waiter Secondary.', message.data);
        this.setSecondary(false);
      }

      if (message.data.repeat === false || message.data.statusTertiary === false) {
        this.logger.debug('Received echo: Cancel Call Waiter Tertiary.', message.data);
        this.setTertiary(false);
      }
    });

    // Flag to prevent sending push notification multiple times
    let pushNotificationCanBeSent = true;

    // Send status to webSocket and push notification
    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => combineLatest([
        this.restaurantTable.table$,
        this.webSocket.status$,
        this.status$,
      ])),
      switchMap(([table, connected, status]) => {
        const anyCallWaiter = CALL_WAITER_DEFINITIONS.some((definition) => status[definition.type]);

        // Reset flag to send push notification when all call waiter is canceled
        if (!anyCallWaiter) {
          pushNotificationCanBeSent = true;
        }

        if (table === null) {
          this.logger.debug('Skip Call Waiter because table is not found.');
          return EMPTY;
        }

        if (connected === false) {
          this.logger.debug('Skip Call Waiter because WebSocket is not connected.');
          return EMPTY;
        }

        this.logger.debug('Current States:', {
          table: table.tableId,
          status,
        });

        // Send status to webSocket and push notification
        this.sendStatus(table, status);

        if (anyCallWaiter) {
          // Send push notification only once
          if (pushNotificationCanBeSent) {
            this.sendPushNotification(table, status);
            pushNotificationCanBeSent = false;
          }

          // Send status to webSocket every 5 seconds
          return interval(5_000).pipe(
            tap(() => this.sendStatus(table, status))
          );
        }

        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  public setMain(status: boolean): void {
    this.setByType('main', status);
  }

  public setSecondary(status: boolean): void {
    this.setByType('secondary', status);
  }

  public setTertiary(status: boolean): void {
    this.setByType('tertiary', status);
  }

  public syncAmbientLight(): void {
    this.status$.pipe(
      take(1),
    ).subscribe((status) => {
      this.sendAmbientLight(this.getAmbientLightColor(status));
    });
  }

  public getTrackingName(type: CallWaiterType, active: boolean): string {
    return `${ active ? 'Cancel ' : 'Start ' }${ this.actionNamesSubject.value[type] }`;
  }

  private sendStatus(table: IRestaurantTable, status: CallWaiterStatus): void {
    this.webSocket.send('echo', {
      tableId: table.tableId,
      statusMain: status.main,
      statusSecondary: status.secondary,
      statusTertiary: status.tertiary,
    });
  }

  private sendPushNotification(table: IRestaurantTable, status: CallWaiterStatus): void {
    const language = this.translate.getFallbackLang() ?? 'en-CA';

    this.translateLoader.getTranslation(language).pipe(
      map((translations) => {
        const translationsPushNotification = translations['pushNotification'];
        const pushNotificationText = (
          translationsPushNotification
          && isObject(translationsPushNotification)
          && !Array.isArray(translationsPushNotification)
        )
          ? translationsPushNotification as Partial<Record<CallWaiterType, string>>
          : {};

        return CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
          if (status[definition.type]) {
            const action = table.features?.callWaiter?.[definition.type];

            acc.push(
              action?.translations?.[language]
              || pushNotificationText[definition.type]
              || DEFAULT_PUSH_NOTIFICATION_TEXT[definition.type],
            );
          }

          return acc;
        }, [] as string[]);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((messages) => {
      if (messages.length > 0) {
        this.webSocket.send('pushNotification', {
          title: `Table ${ table.tableName }`,
          body: messages.join(', '),
          recipientsCategory: 'all',
        });
      }
    });
  }

  private getAmbientLightColor(status: CallWaiterStatus): RGBA | null {
    for (const definition of CALL_WAITER_DEFINITIONS) {
      const color = this.ambientLightColorSubjects[definition.type].value;
      if (status[definition.type] && color) {
        return color;
      }
    }

    return null;
  }

  private sendAmbientLight(color: RGBA | null): void {
    if (color) {
      this.intercom.call('ambient_light.on', { color });
    }
    else {
      this.intercom.call('ambient_light.off');
    }
  }

  private setByType(type: CallWaiterType, status: boolean): void {
    this.logger.info(status ? `Call Waiter ${ type }` : `Cancel ${ type } Call Waiter`);
    this.cluster.broadcast('waiter.call', { type, status }).then();
    this.statusSubjects[type].next(status);
  }

  private withCallWaiterByType(type: CallWaiterType): Observable<boolean> {
    const definition = CALL_WAITER_DEFINITION_BY_TYPE[type];
    const defaultEnabled = this.featureToggle.withCallWaiter[definition.type];

    return this.restaurantTable.table$.pipe(
      map((table) => this.getFeatureEnabled(table?.features, definition, defaultEnabled)),
      startWith(defaultEnabled),
      distinctUntilChanged(),
      switchMap((enabled) => {
        if (!enabled) {
          return of(false);
        }

        if (this.featureToggle.withCallWaiterOnOffline) {
          return of(true);
        }

        return this.network.status$;
      }),
      shareReplay(1),
    );
  }

  private getFeatureEnabled(
    features: IFeature | undefined,
    definition: CallWaiterDefinition,
    defaultEnabled: boolean,
  ): boolean {
    if (!features) {
      return defaultEnabled;
    }

    return features.callWaiter?.[definition.type]?.enabled
      ?? features[definition.featureKey]
      ?? defaultEnabled;
  }

  private getActionNames(features: IFeature | undefined): Record<CallWaiterType, string> {
    return CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.type] = features?.callWaiter?.[definition.type]?.name
        || DEFAULT_CALL_WAITER_ACTION_NAMES[definition.type];
      return acc;
    }, {} as Record<CallWaiterType, string>);
  }

  private createSubjects<T>(initialValue: T): Record<CallWaiterType, BehaviorSubject<T>> {
    return CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.type] = new BehaviorSubject<T>(initialValue);
      return acc;
    }, {} as Record<CallWaiterType, BehaviorSubject<T>>);
  }

  private createStatusByTypeStreams(): Record<CallWaiterType, Observable<boolean>> {
    return CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.type] = this.statusSubjects[definition.type].asObservable().pipe(
        distinctUntilChanged(),
      );
      return acc;
    }, {} as Record<CallWaiterType, Observable<boolean>>);
  }

  private createCountByTypeStreams(): Record<CallWaiterType, Observable<number>> {
    return CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.type] = this.countSubjects[definition.type].asObservable();
      return acc;
    }, {} as Record<CallWaiterType, Observable<number>>);
  }

  private createLastCallAtByTypeStreams(): Record<CallWaiterType, Observable<Date | null>> {
    return CALL_WAITER_DEFINITIONS.reduce((acc, definition) => {
      acc[definition.type] = this.lastCallAtSubjects[definition.type].asObservable();
      return acc;
    }, {} as Record<CallWaiterType, Observable<Date | null>>);
  }

}
